/* eslint-disable @typescript-eslint/camelcase */
import { inject } from 'inversify-props'
import API from '@core/services/API'
import { Component, Vue, Watch } from 'vue-property-decorator'
import AppForm from '@core/components/AppFormV2.vue'
import AppDatatable from '@core/components/AppDatatable.vue'
import AppFormLayout from '@core/components/layout/AppFormLayout.vue'
import AppPdfViewer from '@core/components/AppPdfViewer.vue'
import LoadingSpinner from '@core/components/LoadingSpinner.vue'
import AppFormToolBar from '@core/components/AppFormToolBar.vue'
import AppModal from '@core/components/AppModalV2.vue'
import RecruitFormsNavi from './RecruitFormsNavi.vue'
import RecruitFormsUsher from './RecruitFormsUsher.vue'
import { IDateService } from '@master/services/IDateService'
import { FormBaseMixin } from './forms/FormBaseMixin'
import AppSignatureBox from '@core/components/AppSignatureBoxV2.vue'
import { namespace } from 'vuex-class'
import { Route } from 'vue-router'
import { FormBase } from '@core/model/recruit/FormBase'
import { AllForms } from '@core/model/recruit/AllForms'
import RecruitDetails from '@core/model/recruit/RecruitDetail'
import { FormDetails } from './RecruitFormStore'
import { UIService } from '@core/services/UIService'
import { SharedVariable } from '@core/model/SharedVariable'
import { isEmpty } from 'lodash/fp'
import { GeneralDeclaration } from '@core/model/recruit/forms/GeneralDeclaration'
import AppDialog from '@core/components/AppDialog.vue'
import { ExamForm } from '@core/model/recruit/ExamForm'

const recruitFormStore = namespace('RecruitFormStore')
const activityStore = namespace('ActivityStore')
/**
 * @TODO: To understand how the current form errors works.
 */
@Component({
  components: {
    AppForm,
    AppDatatable,
    AppFormLayout,
    AppPdfViewer,
    LoadingSpinner,
    AppFormToolBar,
    AppModal,
    AppSignatureBox,
    RecruitFormsNavi,
    RecruitFormsUsher,
    AppDialog
  },
  beforeRouteUpdate (to: Route, from: Route, next: any) {
    this.checkFad()
    this.saveForm()
    next()
  }
})
export class RecruitFormsMixin extends Vue {
  @inject()
  protected dateService: IDateService

  @inject()
  protected $api: API

  uiService: UIService

  @inject()
  private sharedVariable: SharedVariable

  $refs!: {
    activeForm: FormBaseMixin;
  }

  @recruitFormStore.State
  private forms: AllForms

  @recruitFormStore.State
  private exams: ExamForm[]

  @recruitFormStore.State
  private recruitDetails: RecruitDetails

  @recruitFormStore.Mutation
  private setForms

  @recruitFormStore.Mutation
  private addExistingExams

  @recruitFormStore.Mutation
  private setRecruitDetails

  @recruitFormStore.Mutation
  private setCurUser

  @recruitFormStore.Getter
  private formsDetails: FormDetails[]

  @recruitFormStore.Getter
  private createdForms: FormDetails[]

  @recruitFormStore.Getter
  private isDirty

  @recruitFormStore.Getter
  private allSigned

  @activityStore.Mutation
  private upsertActivity

  @activityStore.Mutation
  private addActivity

  @activityStore.Mutation
  private resetActivities

  @activityStore.State
  private activities

  @activityStore.Getter
  private allActivityPending

  @activityStore.Getter
  private allActivityProcessing

  @activityStore.Getter
  private allActivityInReadyForSubmission

  @activityStore.Getter
  private allActivityCompleted

  protected showLeft = true
  protected showSignPad = false
  protected savingSignature = false
  protected refreshForm = 0
  protected refreshToolbar = 0
  protected loading = false
  protected currentForm = null
  protected isModalFad = false
  protected fadWarning = []

  get recruitAccountOid () {
    return this.$router.currentRoute.params.recruitAccountOid
  }

  get currentFormSigned () {
    return this.currentForm?.data?.signed
  }

  get currentFormName () {
    return this.currentForm?.displayName
  }

  get currentFormErrors () {
    return this.currentForm?.data.error || []
  }

  get activeFormPdfUri () {
    return `pdf/stampedform?template=${this.currentForm?.data.pdfName}&ownerOid=${this.recruitAccountOid}`
  }

  async triggerAlert (message: string, type: 'error' | 'warning' = 'error') {
    await this.uiService.alert.show({
      type,
      message
    })
  }

  getRecruitDetail () {
    if (this.currentUser) {
      this.$api.recruit.detail
        .retrieve({ recruitOid: this.recruitAccountOid })
        .then(res => {
          if (res.error) return this.triggerAlert(res.error)
          this.setRecruitDetails(res)
        })
    }
  }

  getAllActivities () {
    this.resetActivities()
    if (this.currentUser) {
      this.$api.forms
        .search({
          accountOid: this.recruitAccountOid
        }).then(res => {
          if (res.error) return this.triggerAlert(res.error)
          const forms = []
          res.forEach((form) => {
            forms.push(form)
            this.addActivity(form.activity)
          })
          this.setForms(forms)
        }).catch((err) => { this.triggerAlert(err) })

      this.$api.recruit.exam.search({
        accountOid: this.recruitAccountOid
      }).then(res => {
        if (res.error) return this.triggerAlert(res.error)
        this.addExistingExams(res.recs)
      }).catch((err) => { this.triggerAlert(err) })
    }
  }

  get currentUser () {
    return this.$store.getters.currentUser
  }

  get recruitNeedSign () {
    const signerRequired = Object.keys(this.currentForm.data.signatures)
    return signerRequired.includes('applicant') && this.currentFormErrors.length === 0 && !this.currentForm.data.signed
  }

  get recruiterNeedSign () {
    const signerRequired = Object.keys(this.currentForm.data.signatures)
    if (!signerRequired.includes('recruiter')) return false
    return isEmpty(this.currentForm.data.signatures.recruiter)
  }

  get canSign () {
    if (!this.currentForm || this.isDirty || this.currentFormErrors.length > 0) return false
    const recruitSign = this.recruitDetails.isCurrentRecruit && this.recruitNeedSign
    const recruiterSign = this.recruitDetails.isCurrentRecruiter && this.recruiterNeedSign
    return (recruitSign || recruiterSign) && !this.currentForm.data.signed
  }

  get adminAction () {
    // @TODO : to confirm T3 role
    return this.recruitDetails.isAdmin && this.allSigned && this.allActivityProcessing
  }

  get overallFormStatus () {
    if (this.allActivityInReadyForSubmission) return 'Ready for Submission'
    if (this.canSign || this.adminAction) return undefined
    if (this.currentFormErrors.length > 0) return 'Required fields not completed'
    if (this.recruitNeedSign) return 'Pending Applicant Signature'
    if (this.recruiterNeedSign) return 'Pending Recruiting Manager Signature'
    if (this.allActivityCompleted) return 'APPROVED'
  }

  get fad () {
    return (this.forms.getForm(GeneralDeclaration) as GeneralDeclaration).data.fad
  }

  @Watch('currentUser', { immediate: true, deep: true })
  curUser (curUser) {
    this.setCurUser(curUser)
  }

  @Watch('$route', { immediate: true })
  routeUpdated (to) {
    this.setCurrentForm(to.name)
  }

  @Watch('fad', { deep: true })
  onChangeMedisave () {
    this.checkFad()
  }

  created () {
    this.getRecruitDetail()
    this.getAllActivities()
    this.uiService = this.sharedVariable.uiService
  }

  checkFad () {
    this.fadWarning = []
    if (this.fad.medisaveOs === 'yes' || this.fad.basedOverseas === 'yes') this.showModalFad()
  }

  showModalFad () {
    this.isModalFad = true
    if (this.fad.medisaveOs === 'yes') this.fadWarning.push('Do you have any outstanding CPF MediSave liabilities with CPF Board?')
    if (this.fad.basedOverseas === 'yes') this.fadWarning.push('Will you be based overseas?')
  }

  triggerUnsavedChanges (e) {
    if (this.allActivityCompleted || !this.isDirty) return
    this.triggerAlert('Alert : You have unsaved changes !')
    e.preventDefault()
    e.returnValue = 'Hey'
  }

  toggleLeft (expanded) {
    this.refreshToolbar++
    this.showLeft = expanded
  }

  setCurrentForm (name) {
    this.currentForm = this.formsDetails.find(f => f.name === name)
  }

  showMsg (form) {
    this.$router.push({ name: form.name })
    this.$refs.activeForm.$data.showError = true
  }

  clickSign () {
    this.saveForm()
    this.showSignPad = true
  }

  getRemovedCertificates () {
    const currentCertificates = this.forms.qualificationEmployment.data.professional
    const removedCertificates = this.exams.filter(exam => !currentCertificates.some(curCert => curCert.value === exam.name))
    return removedCertificates
  }

  async saveForm () {
    let removedCertificates = []
    if (this.$router.currentRoute.name === 'recruit-forms-qualification-employment') removedCertificates = this.getRemovedCertificates()

    // Confirm delete removed certificates
    if (removedCertificates.length) {
      const certificatesName = removedCertificates.map(certificate => certificate.name)
      const isConfirm = await this.uiService.confirm.show({
        type: 'warning',
        message: `Are you sure to delete ${certificatesName.length > 1 ? 'these' : 'this'} (${certificatesName.join(',')}) exam certification(s)?`
      })
      if (isConfirm) {
        if (!this.isDirty) return
        this.$refs.activeForm.save()
      }
    } else {
      if (!this.isDirty) return
      this.$refs.activeForm.save()
    }
  }

  saveSignature (sign) {
    this.savingSignature = true
    this.$api.forms.sign({
      recruitOid: this.recruitAccountOid,
      signature: sign
    }).then((res) => {
      if (res?.error) return this.triggerAlert(res.error)
      this.getAllActivities()
    }).catch((err) => {
      this.triggerAlert(err)
    }).finally(() => {
      this.savingSignature = false
      this.showSignPad = false
    })
  }

  approve () {
    if (!confirm('Confirm to approve? By confirming, you must already completed all the recruit check in the recruit\'s profile and view through all the exam certificates and documents.\n\n This action will change the recruit\'s status to be an adviser.')) return
    // @TODO: To be reviewed again because this flow is unsure of.
    // if (this.fadWarning) {
    //   this.isModalFad = true
    //   return
    // }
    this.loading = true
    this.$api.recruit.detail.approve({
      accountOid: this.recruitAccountOid
    })
      .then(res => {
        if (res?.error) return this.triggerAlert(res.error)
        this.getAllActivities()
      }).catch(err => {
        if (err) return this.triggerAlert(err)
      }).finally(() => {
        this.loading = false
      })
  }

  rework () {
    if (!confirm('Confirm to rework?')) return
    this.loading = true
    this.$api.activity
      .decide({
        ids: Object.values(this.forms).map(form => (form as FormBase).activityId),
        status: 'PENDING'
      })
      .then(res => {
        if (res?.error) return this.triggerAlert(res.error)
        // trigger call to update recruit status back to pending
        this.$api.recruit.detail.reworkApplication({
          accountOid: this.recruitAccountOid
        })
        this.getAllActivities()
      }).catch(err => {
        if (err) return this.triggerAlert(err)
      }).finally(() => {
        this.loading = false
      })
  }
}
